import { Fragment, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Website } from '@/api/types';
import { Button, Icon, sectionType, Thumb, useEscapeLayer, useToast } from '@/design-system';
import type { PinnedPreset } from './api';
import { GalleryFilterRow, GalleryGrid, useCountLine, useGallery, WIDE_TYPES, type GalleryFilters } from './Gallery';
import { useStructureLabels } from './labels';
import { useSectionTypeKeys } from './Outline';
import { activate, specOf } from './PageStack';
import { PrefsButton, type PrefSummary } from './parts';
import { blankSection, insertSection, moveSection, primary, removeSection, setPrimary, updateAt, type StructureDoc } from './useStructure';
import s from './constructor.module.css';

/** The drawer: adding a section at an insertion point, or changing the design of section `i`. */
type DrawerState = { mode: 'add'; at: number } | { mode: 'swap'; i: number } | null;

type Props = {
  site: Website;
  doc: StructureDoc;
  summary: PrefSummary;
  filters: GalleryFilters;
  setFilters: (f: GalleryFilters) => void;
  onClose: () => void;
  onPrefs: () => void;
  /** «Сгенерировать»: the caller closes the constructor and generates */
  onGenerate: () => void;
};

const stop = (e: MouseEvent) => e.stopPropagation();

/**
 * «Конструктор страницы» (DESIGN_SPEC §4.6 Constructor): a full-screen white sheet. «+ Добавить» sits
 * before, between and after the sections (the active one reads «Сюда»); the right drawer offers the
 * library by section type, and hovering a design shows a ghost at the insertion point (or swaps the
 * section in place). A selected section can change its design, move up or down, or go. Esc closes the
 * innermost layer (preferences → drawer → constructor); every change saves automatically.
 */
export function Constructor({ site, doc, summary, filters, setFilters, onClose, onPrefs, onGenerate }: Props) {
  const { t } = useTranslation('structure');
  const toast = useToast();
  const { section, designName, designLabel } = useStructureLabels();
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [cat, setCat] = useState('hero');
  const [hov, setHov] = useState<PinnedPreset | null>(null);
  const secs = doc.secs;
  const n = secs.length;
  const insertAt = drawer?.mode === 'add' ? drawer.at : null;
  const swapAt = drawer?.mode === 'swap' ? drawer.i : null;
  const galleryType = swapAt !== null ? (secs[swapAt]?.section_type ?? cat) : cat;

  useEscapeLayer(true, onClose);
  useEscapeLayer(!!drawer, () => closeDrawer());

  const closeDrawer = () => {
    setDrawer(null);
    setHov(null);
  };
  const openAdd = (at: number, kind?: string) => {
    setDrawer({ mode: 'add', at });
    setSel(null);
    setHov(null);
    setFilters({ ...filters, layout: 'all' });
    setCat(kind ?? (at === 0 ? 'header' : at === n ? 'footer' : cat === 'header' || cat === 'footer' ? 'about' : cat));
  };
  const openSwap = (i: number) => {
    setDrawer({ mode: 'swap', i });
    setSel(i);
    setHov(null);
    setFilters({ ...filters, layout: 'all' });
  };
  const pick = (pin: PinnedPreset | null) => {
    if (swapAt !== null) {
      doc.update((list) => updateAt(list, swapAt, (x) => setPrimary(x, pin)));
      closeDrawer();
      return toast(t('cz.swapped'));
    }
    const at = insertAt ?? n;
    doc.update((list) => insertSection(list, blankSection(galleryType, pin ? [pin] : []), at));
    setSel(at);
    closeDrawer();
    toast(t('cz.added', { section: section(galleryType) }));
  };
  const move = (i: number, to: number) => {
    if (to < 0 || to >= n) return;
    doc.update((list) => moveSection(list, i, to));
    setSel(to);
  };
  const remove = (i: number) => {
    doc.update((list) => removeSection(list, i));
    setSel(null);
  };
  const clear = () => {
    doc.update(() => []);
    setDrawer(null);
    setSel(null);
    toast(t('cz.cleared'));
  };

  return createPortal(
    <div className={s.overlay} role="dialog" aria-modal="true" aria-label={t('cz.label')}>
      <div className={s.top}>
        <button type="button" className={s.close} title={t('cz.exit')} aria-label={t('cz.exit')} onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
        <div className={s.titles}>
          <h2 className={s.title}>{t('cz.title')}</h2>
          <span className={s.sub}>{t('cz.sub', { site: site.name, sections: t('sections', { count: n }) })}</span>
        </div>
        <div className={s.actions}>
          <PrefsButton summary={summary} onClick={onPrefs} />
          <Button variant="secondary" icon="eraser" iconSize={15} onClick={clear}>
            {t('cz.clear')}
          </Button>
          <Button icon="sparkles" iconSize={15} onClick={onGenerate}>
            {t('cz.generate')}
          </Button>
        </div>
      </div>
      <div className={s.body}>
        <div className={s.canvas} onClick={() => setSel(null)}>
          <div className={s.wrap} data-drawer={!!drawer}>
            {n === 0 ? (
              <div className={s.empty}>
                <button type="button" className={s.bigPlus} aria-label={t('cz.add_first')} onClick={(e) => (stop(e), openAdd(0))}>
                  <Icon name="plus" size={26} />
                </button>
                <span className={s.emptyTitle}>{t('cz.empty_title')}</span>
                <span className={s.emptyHint}>{t('cz.empty_hint')}</span>
                <div className={s.quick}>
                  {(['header', 'hero', 'featured'] as const).map((k) => (
                    <button key={k} type="button" className={s.quickChip} onClick={(e) => (stop(e), openAdd(0, k))}>
                      {t('cz.quick', { section: section(k) })}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className={s.sheet} role="list" aria-label={t('cz.sheet_label')}>
                {Array.from({ length: n + 1 }, (_, i) => {
                  const x = secs[i];
                  const active = insertAt === i;
                  const trying = swapAt === i && hov;
                  const p = trying ? hov : primary(x);
                  const isSel = sel === i || swapAt === i;
                  const def = x ? sectionType(x.section_type) : null;
                  return (
                    <Fragment key={x?.key ?? 'end'}>
                      <div className={s.plusRow}>
                        <button type="button" className={s.plus} title={t('cz.add_title')} aria-pressed={active} data-insert={i} onClick={(e) => (stop(e), openAdd(i))}>
                          <Icon name="plus" size={14} />
                          {active ? t('cz.here') : t('cz.add')}
                        </button>
                      </div>
                      {active && hov && (
                        <div className={s.ghost} style={{ aspectRatio: sectionType(galleryType).previewRatio }}>
                          <Thumb {...specOf(hov)} className={s.fill} style={{ position: 'absolute' }} />
                          <span className={s.ghostTag}>{t('cz.ghost', { design: designName(hov) })}</span>
                        </div>
                      )}
                      {x && def && (
                        <div
                          role="listitem"
                          aria-selected={isSel}
                          aria-label={section(x.section_type)}
                          tabIndex={0}
                          className={s.sec}
                          style={{ aspectRatio: def.previewRatio }}
                          onClick={(e) => {
                            stop(e);
                            setSel(i);
                            setDrawer(null);
                            setHov(null);
                          }}
                          onKeyDown={(e) => activate(e, () => setSel(i))}
                        >
                          <Thumb {...specOf(p)} className={s.fill} style={{ position: 'absolute' }} />
                          {!p && (
                            <span className={s.veil}>
                              <span className={s.aiChip}>
                                <Icon name="sparkles" size={12} />
                                {t('cz.ai')}
                              </span>
                            </span>
                          )}
                          <span className={s.border} />
                          <span className={s.tag}>
                            <Icon name={def.icon} size={12} />
                            {section(x.section_type)}
                            <span className={s.tagLabel}>· {trying ? t('cz.trying') : p ? designLabel(p) : t('outline.ai')}</span>
                          </span>
                          {isSel && (
                            <div className={s.tools} role="group" aria-label={t('cz.tools_label')}>
                              <button type="button" className={s.swapBtn} onClick={(e) => (stop(e), openSwap(i))}>
                                <Icon name="palette" size={13} />
                                {t('cz.swap')}
                              </button>
                              <button type="button" className={s.tool} title={t('cz.up')} aria-label={t('cz.up')} disabled={i === 0} onClick={(e) => (stop(e), move(i, i - 1))}>
                                <Icon name="arrow-up" size={14} />
                              </button>
                              <button type="button" className={s.tool} title={t('cz.down')} aria-label={t('cz.down')} disabled={i === n - 1} onClick={(e) => (stop(e), move(i, i + 1))}>
                                <Icon name="arrow-down" size={14} />
                              </button>
                              <button type="button" className={s.toolDel} title={t('cz.delete')} aria-label={t('cz.delete')} onClick={(e) => (stop(e), remove(i))}>
                                <Icon name="trash-2" size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {drawer && (
          <ConstructorDrawer
            title={swapAt !== null ? t('cz.drawer_swap', { section: section(galleryType) }) : t('cz.drawer_add', { n: (insertAt ?? 0) + 1 })}
            type={galleryType}
            adding={drawer.mode === 'add'}
            onCat={(k) => {
              setCat(k);
              setHov(null);
              setFilters({ ...filters, layout: 'all' });
            }}
            pinned={swapAt !== null ? secs[swapAt]?.presets : undefined}
            filters={filters}
            setFilters={setFilters}
            onPick={pick}
            onHover={setHov}
            onClose={closeDrawer}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

type DrawerProps = {
  title: string;
  type: string;
  adding: boolean;
  onCat: (k: string) => void;
  pinned?: PinnedPreset[];
  filters: GalleryFilters;
  setFilters: (f: GalleryFilters) => void;
  onPick: (pin: PinnedPreset | null) => void;
  onHover: (pin: PinnedPreset | null) => void;
  onClose: () => void;
};

/** «Добавить секцию · позиция N» / «Сменить дизайн · ‹section›»: the section types (adding), search, style, the 2-column gallery. */
function ConstructorDrawer({ title, type, adding, onCat, pinned, filters, setFilters, onPick, onHover, onClose }: DrawerProps) {
  const { t } = useTranslation('structure');
  const { section } = useStructureLabels();
  const kinds = useSectionTypeKeys();
  const data = useGallery(type, filters);
  const count = useCountLine(data);
  return (
    <aside className={s.drawer} aria-label={title}>
      <div className={s.drHead}>
        <div className={s.drTitles}>
          <h3 className={s.drTitle}>{title}</h3>
          <span className={s.drHint}>{t('cz.drawer_hint')}</span>
        </div>
        <button type="button" className={s.drClose} title={t('cz.close')} aria-label={t('cz.close')} onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
      </div>
      {adding && (
        <div className={s.cats} role="radiogroup" aria-label={t('cz.cats_label')}>
          {kinds.map((k) => (
            <button key={k.key} type="button" role="radio" aria-checked={type === k.key} className={s.cat} onClick={() => onCat(k.key)}>
              <Icon name={k.icon} size={12} />
              {section(k.key)}
            </button>
          ))}
        </div>
      )}
      <GalleryFilterRow filters={filters} onFilters={setFilters} short className={s.drFilters} />
      <div className={s.drBody}>
        <span className={s.drCount}>{count}</span>
        <GalleryGrid type={type} data={data} filters={filters} pinned={pinned} min={WIDE_TYPES.has(type) ? '100%' : '200px'} onPick={(pin) => onPick(pin)} onHover={onHover} />
      </div>
    </aside>
  );
}
